import React, { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import './Login.css';
import backgroundImage from '../../assets/images/Background.jpeg';
import logo from '../../assets/images/logo.png';
import { loginUser, loginWithGoogle, setAuthToken, setRefreshToken, removeAuthToken, removeRefreshToken, requestUnsuspension } from '../../api/services/authService';
import { useAuth } from '../../context/AuthContext';
import { validateDashboardRoleAccess } from '../../utils/roleAccessCheck';
import { shouldRequireSubscriptionGate } from '../../utils/subscriptionAccess';
import { getAvailableDashboards, resolveDestination, fromImpliesTenant } from '../../utils/roleRouting';
import RolePicker from '../../components/RolePicker/RolePicker';
import AgreementGate from './components/AgreementGate';

const normalizeBoolean = (value) => value === true || value === 'true' || value === 1 || value === '1';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showUnsuspensionModal, setShowUnsuspensionModal] = useState(false);
  const [unsuspensionReason, setUnsuspensionReason] = useState('');
  const [submittingUnsuspension, setSubmittingUnsuspension] = useState(false);
  const [unsuspensionMessage, setUnsuspensionMessage] = useState({ text: '', type: '' });
  const [agreementPending, setAgreementPending] = useState(false);
  const [pendingRoute, setPendingRoute] = useState(null);
  const [pendingReturnTo, setPendingReturnTo] = useState(null);
  const [rolePickerRoles, setRolePickerRoles] = useState(null);
  const [rolePickerContext, setRolePickerContext] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || null;
  const { login: authLogin } = useAuth();

  const resolveSubscriptionGateState = useCallback((responseData = {}) => {
    const subscriptionEnabledValue = Object.prototype.hasOwnProperty.call(responseData, 'subscriptionEnabled')
      ? responseData.subscriptionEnabled : undefined;
    const hasActiveSubscriptionValue = Object.prototype.hasOwnProperty.call(responseData, 'hasActiveSubscription')
      ? responseData.hasActiveSubscription : undefined;
    const subscriptionEnabled = subscriptionEnabledValue === undefined ? true : normalizeBoolean(subscriptionEnabledValue);
    const hasActiveSubscription = hasActiveSubscriptionValue === undefined ? false : normalizeBoolean(hasActiveSubscriptionValue);
    return { subscriptionEnabled, hasActiveSubscription };
  }, []);

  /**
   * Run role-access check, subscription gate, agreement gate, and navigate.
   */
  const proceedToDashboard = useCallback(async (dash, userRoles, responseData) => {
    try {
      const roleCheck = await validateDashboardRoleAccess(dash.uiRole, {
        userRoles,
        hasAdminRole: userRoles.includes('ADMIN'),
        isAdminEmail: false,
      });

      if (!roleCheck.hasAccess) {
        removeAuthToken();
        removeRefreshToken();
        throw new Error(roleCheck.message || 'Access denied for the selected role.');
      }

      const finalPath = resolveDestination(dash, from);

      const subscriptionState = resolveSubscriptionGateState(responseData || {});
      const subscriptionRedirectRequired = shouldRequireSubscriptionGate({
        role: dash.uiRole,
        hasActiveSubscription: subscriptionState.hasActiveSubscription,
        subscriptionEnabled: subscriptionState.subscriptionEnabled,
      });

      if (subscriptionRedirectRequired) {
        localStorage.setItem('tenantscore_subscription_state', 'required');
        setRolePickerRoles(null);
        setRolePickerContext(null);
        navigate('/subscription-plans', { state: { returnTo: finalPath }, replace: true });
        return;
      }

      localStorage.removeItem('tenantscore_subscription_state');
      setRolePickerRoles(null);
      setRolePickerContext(null);
      setPendingRoute(finalPath);
      setPendingReturnTo(null);
      setAgreementPending(true);
    } catch (err) {
      throw err;
    }
  }, [from, navigate, resolveSubscriptionGateState]);

  /**
   * Complete the login flow after authentication & phone verification are done.
   * Decides whether to show a role picker (multiple roles) or route directly.
   */
  const completeLogin = useCallback(async (userRoles, responseData, userObj) => {
    const roles = (Array.isArray(userRoles) ? userRoles : []).map((r) => String(r).toUpperCase());
    const dashboards = getAvailableDashboards(roles);

    // Sync AuthContext so ProtectedRoute sees the user as authenticated.
    const token = responseData?.token || responseData?.data?.token;
    const refreshToken = responseData?.refreshToken || responseData?.data?.refreshToken;
    if (token) {
      authLogin(token, refreshToken, userObj || responseData?.user, roles);
    }

    if (dashboards.length === 0) {
      removeAuthToken();
      removeRefreshToken();
      setMessage({ text: 'No dashboard is assigned to your account. Please contact support.', type: 'error' });
      return;
    }

    // If redirected from a property/listing page and the user can act as tenant, auto-route there.
    if (from && fromImpliesTenant(from) && roles.some((r) => r === 'TENANT' || r === 'GUEST')) {
      const dash = dashboards.find((d) => d.apiRole === 'TENANT') || dashboards.find((d) => d.apiRole === 'GUEST');
      if (dash) {
        await proceedToDashboard(dash, roles, responseData);
        return;
      }
    }

    // Single role → route directly.
    if (dashboards.length === 1) {
      await proceedToDashboard(dashboards[0], roles, responseData);
      return;
    }

    // Multiple roles → show the role picker.
    setRolePickerRoles(roles);
    setRolePickerContext({ responseData, userName: userObj?.firstName });
  }, [from, authLogin, proceedToDashboard]);

  const handleRolePickerSelect = useCallback(async (dash) => {
    if (rolePickerContext) {
      await proceedToDashboard(dash, rolePickerRoles, rolePickerContext.responseData);
    }
  }, [rolePickerContext, rolePickerRoles, proceedToDashboard]);

  // ---- Phone-verification detection (shared by login + google login error handling) ----
  const detectPhoneVerificationNeeded = (error) => {
    const msg = (error.message || '').toLowerCase();
    const errorResponse = error.response || {};
    const errorData = errorResponse.data || errorResponse;
    const needsPhoneVerification =
      (msg.includes('phone') && (msg.includes('verify') || msg.includes('verification') || msg.includes('not verified'))) ||
      (msg.includes('otp') && (msg.includes('required') || msg.includes('verify'))) ||
      errorResponse.phoneNotVerified === true ||
      errorResponse.requiresPhoneVerification === true ||
      errorData?.phoneNotVerified === true ||
      errorData?.requiresPhoneVerification === true;
    const phoneNumber = errorData?.phone || errorData?.phoneNumber || errorResponse.phone || errorResponse.phoneNumber || null;
    return { needsPhoneVerification, phoneNumber };
  };

  // ---- Google Sign-In ----
  const handleGoogleSignIn = useCallback(async (response) => {
    try {
      setIsGoogleLoading(true);
      setMessage({ text: '', type: '' });

      const idToken = response.credential;
      const apiResponse = await loginWithGoogle(idToken);

      const user = apiResponse.data?.user || apiResponse.user || {};
      const responseData = apiResponse.data || apiResponse;

      const requiresProfileCompletion = responseData.requiresProfileCompletion === true;
      const requiresOtp = responseData.requiresOtp === true;

      const token = apiResponse.data?.token || apiResponse.token;
      const refreshToken = apiResponse.data?.refreshToken;
      if (token) setAuthToken(token);
      if (refreshToken) setRefreshToken(refreshToken);

      const userRoles = user.roles || responseData.roles || [];

      // Suspension check
      const isActive = responseData.isActive !== undefined ? responseData.isActive : true;
      if (!isActive) {
        setIsGoogleLoading(false);
        setShowUnsuspensionModal(true);
        let cleanMessage = apiResponse.message || 'Your account has been suspended. Please request unsuspension.';
        if (cleanMessage.includes('You can only request account unsuspension at')) {
          cleanMessage = cleanMessage.split('You can only request account unsuspension at')[0].trim();
          if (!cleanMessage) cleanMessage = 'Your account has been suspended. Your access is restricted.';
        }
        setMessage({ text: cleanMessage, type: 'error' });
        return;
      }

      // New user → profile completion
      if (requiresProfileCompletion) {
        setIsGoogleLoading(false);
        navigate('/signup', {
          state: {
            fromGoogle: true,
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
            profilePhotoPath: user.profilePhotoPath,
            userId: user.id,
          },
        });
        return;
      }

      // Phone verification
      if (requiresOtp) {
        const phoneVerified =
          user.phoneVerified === true || user.isPhoneVerified === true ||
          user.phoneVerifiedStatus === 'VERIFIED' || user.phoneVerifiedStatus === 'verified' ||
          responseData.phoneVerified === true || responseData.isPhoneVerified === true;
        const phoneNotVerified =
          user.phoneVerified === false || user.isPhoneVerified === false ||
          user.phoneVerifiedStatus === 'NOT_VERIFIED' || user.phoneVerifiedStatus === 'not_verified' ||
          user.phoneVerifiedStatus === 'PENDING' || user.phoneVerifiedStatus === 'pending' ||
          responseData.phoneVerified === false || responseData.isPhoneVerified === false ||
          responseData.requiresPhoneVerification === true;
        const phoneNumber = user.phone || user.phoneNumber || responseData.phone || responseData.phoneNumber || null;

        if (phoneNotVerified && phoneNumber && !phoneVerified) {
          navigate('/verify-otp', {
            state: { phone: phoneNumber, email: user.email || email, rememberMe, fromGoogle: true },
          });
          return;
        }
      }

      if (rememberMe && user) {
        localStorage.setItem('current_user', JSON.stringify(user));
      }

      // Authenticated — present available dashboards.
      setIsGoogleLoading(false);
      await completeLogin(userRoles, responseData, user);
    } catch (error) {
      const { needsPhoneVerification, phoneNumber } = detectPhoneVerificationNeeded(error);
      if (needsPhoneVerification && phoneNumber) {
        navigate('/verify-otp', {
          state: { phone: phoneNumber, email, rememberMe, fromGoogle: true },
        });
        return;
      }

      if (error.status === 401 || error.status === 403) {
        setMessage({ text: 'Google sign-in failed. Please try again.', type: 'error' });
      } else if (error.isNetworkError) {
        setMessage({ text: error.message || 'Network error. Please check your connection and try again.', type: 'error' });
      } else {
        setMessage({ text: error.message || 'Google sign-in failed. Please try again.', type: 'error' });
      }
    } finally {
      setIsGoogleLoading(false);
    }
  }, [email, rememberMe, navigate, completeLogin]);

  // ---- Email/password login ----
  const handleLogin = async (e) => {
    e.preventDefault();
    setMessage({ text: '', type: '' });

    if (!email || !password) {
      setMessage({ text: 'Please enter email and password', type: 'error' });
      return;
    }

    try {
      setIsLoading(true);
      const response = await loginUser(email.trim(), password);

      const user = response.data?.user || response.user || {};
      const responseData = response.data || response;
      const userRoles = user.roles || responseData.roles || [];

      // Phone verification check
      const phoneVerified =
        user.phoneVerified === true || user.isPhoneVerified === true ||
        user.phoneVerifiedStatus === 'VERIFIED' || user.phoneVerifiedStatus === 'verified' ||
        responseData.phoneVerified === true || responseData.isPhoneVerified === true;
      const phoneNotVerified =
        user.phoneVerified === false || user.isPhoneVerified === false ||
        user.phoneVerifiedStatus === 'NOT_VERIFIED' || user.phoneVerifiedStatus === 'not_verified' ||
        user.phoneVerifiedStatus === 'PENDING' || user.phoneVerifiedStatus === 'pending' ||
        responseData.phoneVerified === false || responseData.isPhoneVerified === false ||
        responseData.requiresPhoneVerification === true;
      const phoneNumber = user.phone || user.phoneNumber || responseData.phone || responseData.phoneNumber || null;

      const token = response.data?.token || response.token;
      const refreshToken = response.data?.refreshToken;

      if (phoneNotVerified && phoneNumber && !phoneVerified) {
        if (token) setAuthToken(token);
        if (refreshToken) setRefreshToken(refreshToken);
        navigate('/verify-otp', {
          state: { phone: phoneNumber, email: email.trim(), rememberMe },
        });
        return;
      }

      if (token) setAuthToken(token);
      if (refreshToken) setRefreshToken(refreshToken);

      if (rememberMe && response.data?.user) {
        localStorage.setItem('current_user', JSON.stringify(response.data.user));
      }

      // Suspension check
      const isActive = responseData.isActive !== undefined ? responseData.isActive : true;
      if (!isActive) {
        setIsLoading(false);
        setShowUnsuspensionModal(true);
        let cleanMessage = response.message || 'Your account has been suspended. Please request unsuspension.';
        if (cleanMessage.includes('You can only request account unsuspension at')) {
          cleanMessage = cleanMessage.split('You can only request account unsuspension at')[0].trim();
          if (!cleanMessage) cleanMessage = 'Your account has been suspended. Your access is restricted.';
        }
        setMessage({ text: cleanMessage, type: 'error' });
        return;
      }

      // Authenticated — present available dashboards.
      setIsLoading(false);
      await completeLogin(userRoles, responseData, user);
    } catch (error) {
      const { needsPhoneVerification, phoneNumber } = detectPhoneVerificationNeeded(error);
      if (needsPhoneVerification && phoneNumber) {
        navigate('/verify-otp', {
          state: { phone: phoneNumber, email: email.trim(), rememberMe },
        });
        return;
      }

      if (error.status === 401 || error.status === 403) {
        setMessage({ text: 'Wrong email or password', type: 'error' });
      } else if (error.isNetworkError) {
        setMessage({ text: error.message || 'Network error. Please check your connection and try again.', type: 'error' });
      } else {
        setMessage({ text: error.message || 'Login failed. Please try again.', type: 'error' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleAgreementContinue = useCallback((route) => {
    setAgreementPending(false);
    setPendingRoute(null);
    setPendingReturnTo(null);
    if (route) {
      navigate(route, pendingReturnTo ? { state: { returnTo: pendingReturnTo } } : undefined);
    } else {
      navigate('/');
    }
  }, [navigate, pendingReturnTo]);

  const handleAgreementCancel = useCallback(() => {
    setAgreementPending(false);
    setPendingRoute(null);
    setPendingReturnTo(null);
    removeAuthToken();
    removeRefreshToken();
    setMessage({ text: 'You can sign in again after accepting the agreement.', type: 'info' });
  }, []);

  const handleSignUp = () => navigate('/signup');

  const handleUnsuspensionSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmittingUnsuspension(true);
      setUnsuspensionMessage({ text: '', type: '' });
      const response = await requestUnsuspension(unsuspensionReason);
      if (response.status === 'success') {
        setUnsuspensionMessage({ text: response.message || 'Unsuspension request submitted successfully. Please wait for admin approval.', type: 'success' });
        setUnsuspensionReason('');
        setTimeout(() => {
          setShowUnsuspensionModal(false);
          removeAuthToken();
          removeRefreshToken();
          setMessage({ text: '', type: '' });
        }, 3000);
      } else {
        setUnsuspensionMessage({ text: response.message || 'Failed to submit unsuspension request', type: 'error' });
      }
    } catch (error) {
      setUnsuspensionMessage({ text: error.message || 'Failed to submit unsuspension request. Please try again.', type: 'error' });
    } finally {
      setSubmittingUnsuspension(false);
    }
  };

  const handleCloseUnsuspensionModal = () => {
    setShowUnsuspensionModal(false);
    setUnsuspensionReason('');
    setUnsuspensionMessage({ text: '', type: '' });
    removeAuthToken();
    removeRefreshToken();
  };

  return (
    <div className="login-container">
      <div className="login-background-blur" style={{ backgroundImage: `url(${backgroundImage})` }}></div>
      <div className="login-overlay">
        <div className="login-wrapper">
          {/* Left Section - Welcome */}
          <div className="login-welcome-section">
            <div className="welcome-content">
              <img src={logo} alt="Tenant Score Logo" className="welcome-logo" />
              <div className="welcome-text">
                <span className="welcome-hello">Hello</span>
                <span className="welcome-main">welcome</span>
              </div>
              <div className="contact-separator"></div>
              <div className="contact-details-section">
                <h3 className="contact-title">Contact Details</h3>
                <p className="contact-address">P. O. Box 12345, Nairobi, Kenya</p>
                <div className="contact-info">
                  <span className="contact-label">Email:</span>
                  <a href="mailto:info@tenantscore.com" className="contact-value contact-link">info@tenantscore.com</a>
                </div>
                <div className="contact-info">
                  <span className="contact-label">Tel:</span>
                  <a href="tel:+254113886814" className="contact-value contact-link">+254113886814</a>
                  <span className="contact-separator-inline">|</span>
                  <span className="contact-label">Cell:</span>
                  <a href="tel:+254113886814" className="contact-value contact-link">+254113886814</a>
                </div>
                <div className="contact-info">
                  <span className="contact-label">WhatsApp:</span>
                  <a href="https://wa.me/254113886814" className="contact-value contact-link">+254113886814</a>
                </div>
              </div>
            </div>
          </div>

          {/* Right Section - Login Form */}
          <div className="login-form-section">
            <div style={{ position: 'relative', width: '100%' }}>
              <form className="login-form" onSubmit={handleLogin}>
                <h2 className="form-title">Sign into your account</h2>

                {message.text && (
                  <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-error'}`}>
                    {message.text}
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    className="form-input"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="password">Enter Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      className="form-input"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-7-11-7a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 7 11 7a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <line x1="1" y1="1" x2="23" y2="23" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      ) : (
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="12" cy="12" r="3" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <div className="form-options">
                  <label className="remember-me">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                    />
                    <span>Remember me</span>
                  </label>
                  <button type="button" className="forgot-password" onClick={() => navigate('/forgot-password')}>
                    Forgot Password
                  </button>
                </div>

                <div className="form-buttons">
                  <button type="submit" className="btn-login" disabled={isLoading}>
                    {isLoading ? 'Logging in...' : 'Login'}
                  </button>
                  <button type="button" className="btn-signup" onClick={handleSignUp}>
                    Sign Up
                  </button>
                </div>

                <div className="divider-section">
                  <div className="divider-line"></div>
                  <span className="divider-text">or</span>
                  <div className="divider-line"></div>
                </div>

                <div className="google-login-wrapper">
                  <GoogleLogin
                    onSuccess={(credentialResponse) => handleGoogleSignIn(credentialResponse)}
                    onError={() => {
                      setMessage({ text: 'Google sign-in failed. Please try again.', type: 'error' });
                    }}
                    width="320"
                    text="signin_with"
                    shape="rectangular"
                    size="large"
                    theme="outline"
                    locale="en"
                    disabled={isGoogleLoading || isLoading}
                  />
                  {isGoogleLoading && (
                    <div className="google-loading-overlay">Signing in…</div>
                  )}
                </div>
              </form>

              {agreementPending && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(255,255,255,0.96)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: 'max(12px, env(safe-area-inset-top, 0px)) 16px max(12px, env(safe-area-inset-bottom, 0px)) 16px', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <AgreementGate
                    onContinue={handleAgreementContinue}
                    onCancel={handleAgreementCancel}
                    roleRoute={pendingRoute}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Role picker — shown after login when user has multiple roles */}
      {rolePickerRoles && (
        <RolePicker
          roles={rolePickerRoles}
          onSelect={handleRolePickerSelect}
          userName={rolePickerContext?.userName}
        />
      )}

      {/* Unsuspension Request Modal */}
      {showUnsuspensionModal && (
        <div className="unsuspension-modal-overlay" onClick={handleCloseUnsuspensionModal}>
          <div className="unsuspension-modal" onClick={(e) => e.stopPropagation()}>
            <div className="unsuspension-modal-header">
              <h2 className="unsuspension-modal-title">Account Suspended</h2>
              <button className="unsuspension-modal-close" onClick={handleCloseUnsuspensionModal}>
                ×
              </button>
            </div>
            <div className="unsuspension-modal-body">
              <p className="unsuspension-message">
                Your account has been suspended. Your access is restricted. Please submit a request to unsuspend your account.
              </p>

              {unsuspensionMessage.text && (
                <div className={`unsuspension-alert ${unsuspensionMessage.type === 'success' ? 'unsuspension-alert-success' : 'unsuspension-alert-error'}`}>
                  {unsuspensionMessage.text}
                </div>
              )}

              <form onSubmit={handleUnsuspensionSubmit} className="unsuspension-form">
                <div className="form-group">
                  <label htmlFor="unsuspension-reason" className="form-label">
                    Reason for Unsuspension Request (Optional)
                  </label>
                  <textarea
                    id="unsuspension-reason"
                    className="form-textarea"
                    rows="4"
                    value={unsuspensionReason}
                    onChange={(e) => setUnsuspensionReason(e.target.value)}
                    placeholder="Please provide a reason for your unsuspension request..."
                    disabled={submittingUnsuspension}
                  />
                </div>

                <div className="unsuspension-form-actions">
                  <button
                    type="button"
                    className="unsuspension-btn-cancel"
                    onClick={handleCloseUnsuspensionModal}
                    disabled={submittingUnsuspension}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="unsuspension-btn-submit"
                    disabled={submittingUnsuspension}
                  >
                    {submittingUnsuspension ? 'Submitting...' : 'Submit Request'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
